import { Form } from 'antd';
import { useMemo } from 'react';
import { useKimLhcAutoCalculate, BaseCalculationPayload } from '@/components/views/Report/KimLhc/hooks/useKimLhcAutoCalculate';
import { FieldNamesEnum } from '@/components/views/Report/KimLhc/types';

interface CalculatedScores {
	bodyPostureScore: number;
	additionalFactorsScore: number;
	manScore: number;
	womanScore: number;
}

interface PostureFormData {
	body_posture?: string;
	posture_additional_factor_twist?: string | null;
	posture_additional_factor_load_center?: string | null;
	posture_additional_factor_arms?: string | null;
	posture_additional_factor_hands?: string | null;
}

interface CalculateScoresResponse {
	body_posture_score: number;
	posture_additional_factor_score: number;
	man_score: number;
	woman_score: number;
}

const INITIAL_SCORES: CalculatedScores = {
	bodyPostureScore: 0,
	additionalFactorsScore: 0,
	manScore: 0,
	womanScore: 0
};

function buildPayload(formData: PostureFormData, basePayload: BaseCalculationPayload) {
	return {
		...basePayload,
		body_posture: formData.body_posture,
		posture_additional_factor_twist: formData.posture_additional_factor_twist ?? null,
		posture_additional_factor_load_center: formData.posture_additional_factor_load_center ?? null,
		posture_additional_factor_arms: formData.posture_additional_factor_arms ?? null,
		posture_additional_factor_hands: formData.posture_additional_factor_hands ?? null
	};
}

function transformScores(scores: CalculateScoresResponse): CalculatedScores {
	return {
		bodyPostureScore: scores.body_posture_score,
		additionalFactorsScore: scores.posture_additional_factor_score,
		manScore: scores.man_score,
		womanScore: scores.woman_score
	};
}

export function useAutoCalculate() {
	const form = Form.useFormInstance();
	const POSTURE_GROUP = FieldNamesEnum.POSTURE;

	const bodyPosture = Form.useWatch([POSTURE_GROUP, 'body_posture'], form);
	const postureAdditionalFactorTwist = Form.useWatch([POSTURE_GROUP, 'posture_additional_factor_twist'], form) ?? null;
	const postureAdditionalFactorLoadCenter = Form.useWatch([POSTURE_GROUP, 'posture_additional_factor_load_center'], form) ?? null;
	const postureAdditionalFactorArms = Form.useWatch([POSTURE_GROUP, 'posture_additional_factor_arms'], form) ?? null;
	const postureAdditionalFactorHands = Form.useWatch([POSTURE_GROUP, 'posture_additional_factor_hands'], form) ?? null;

	const watchedValues = useMemo(
		() => ({
			body_posture: bodyPosture,
			posture_additional_factor_twist: postureAdditionalFactorTwist,
			posture_additional_factor_load_center: postureAdditionalFactorLoadCenter,
			posture_additional_factor_arms: postureAdditionalFactorArms,
			posture_additional_factor_hands: postureAdditionalFactorHands
		}),
		[bodyPosture, postureAdditionalFactorTwist, postureAdditionalFactorLoadCenter, postureAdditionalFactorArms, postureAdditionalFactorHands]
	);

	return useKimLhcAutoCalculate<PostureFormData, CalculatedScores, CalculateScoresResponse>({
		watchedValues,
		initialScores: INITIAL_SCORES,
		buildPayload,
		transformScores
	});
}
